import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardActions, Box, Button, Link, Typography, Stack } from '@mui/material';
import { DtLink } from '../ui/link';
import DTDataGrid from "../../components/shared/config/DTDataGrid";
import { IExpression, IPackage, IControlEvaluationResult, IDeliveryType, IOtherControl } from "../../models/configuration/plugin/IConfiguration";
import { evaluationResultsColumns, validDeliveryMethodsColumns } from "./EvaluationResultsData";
import { IExpressionValidationResult } from '../../models/IExpressionValidation';
import expressionService from '../../services/expressionService';
import { useSelector } from 'react-redux';
import { store } from '../../state/store';
import { DtDialog } from '../ui/dialog';
import DtTabs from '../ui/tab/DtTabs';
import ExpressionEditor from '../shared/config/ExpressionEditor';
import { CONFIGURATION } from '../../utils/constants';
import { GetControlOptions } from "../../utils/controlOptionsHelper";
import { GetExpressionEvaluationResults, addRegexExp } from "../../utils/expressionEvaluationHelper";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import { useEnhancedFieldIdValuePairs } from "../../hooks/useEnhancedFieldIdValuePairs";
import { DtDataGrid } from '../ui/dataGrid';
import DtAlert from '../ui/alert/DtAlert';

interface EvaluationResultsProps {
    packageList: IPackage[];
    deliveryTypeList: IDeliveryType[] | null;
    otherControlList: IOtherControl[] | null;
}

const initialExpressionState: IExpression = {
    ExpressionName: "",
    ExpressionString: "",
    ExpressionMessage: "",
    Enabled: false
}

const initialEvaluationResult: IControlEvaluationResult = {
    result: false,
    controlType: "",
    id: "",
    group: "",
    message: "",
    originalExpression: ""
}

const EvaluationResults: React.FC<EvaluationResultsProps> = ({ packageList, deliveryTypeList, otherControlList }) => {
    const expService = new expressionService();
    const [tabValue, setTabValue] = React.useState(0);
    const [controlEvaluationResults, setControlEvaluationResults] = useState<Array<IControlEvaluationResult>>([]);
    const [validDeliveryMethods, setValidDeliveryMethods] = useState<Array<IPackage>>([]);
    const [selectedExpression, setSelectedExpression] = useState<IExpression>(initialExpressionState);
    const [allEvaluationResults, setAllEvaluationResults] = useState<Array<IControlEvaluationResult>>([]);
    const [failedEvaluationResults, setFailedEvaluationResults] = useState<Array<IControlEvaluationResult>>([]);
    const [selectedControlTypeRow, setSelectedControlTypeRow] = useState<IControlEvaluationResult>(initialEvaluationResult);
    const [viewExpressionEnabled, setViewExpressionEnabled] = useState(false);
    const [openViewExpression, setOpenViewExpression] = useState(false);
    const [selected, setSelected] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [footerExpDescription, setFooterExpDescription] = useState<string>("");

    const currentAppState = store.getState().appSlice;
    const accessToken = useSelector(() => currentAppState.accessToken);
    const enhancedFieldIdValuePairs = useEnhancedFieldIdValuePairs();
    const initialPackageList: IPackage[] = packageList?.filter(pkg => pkg.Enabled) ?? [];
    const initialDeliveryTypeList: IDeliveryType[] = deliveryTypeList?.filter(dt => dt.Enabled) ?? [];
    const initialOtherTypeList: IOtherControl[] = otherControlList?.filter(dt => dt.Enabled) ?? [];

    //Handle Valid Delivery Methods Tab
    useEffect(() => {
        if (packageList != null) {
            const packageUpdated = getUpdatedPackageId(packageList);
            setValidDeliveryMethods([...packageUpdated]);
        }
    }, [packageList]);

    const handleEvaluationResults = async (packageList: IPackage[], deliveryTypeList: IDeliveryType[], otherControlList: IOtherControl[]) => {
        setLoading(true);
        try {
            const packageTypePromises = await evaluateData(CONFIGURATION.PACKAGE, packageList);
            const deliveryTypePromises = await evaluateData(CONFIGURATION.DELIVERY_TYPE, deliveryTypeList);
            const otherControlPromises = await evaluateData(CONFIGURATION.GENERIC_CONTROL, otherControlList);

            // Combine all promises into a single array
            const evaluationPromises = [packageTypePromises, deliveryTypePromises, otherControlPromises];

            // Await all combined promises and get the results
            const [pkgResult, deliveryTypeResult, otherControlResult] = await Promise.all(evaluationPromises);

            // Combine all evaluation lists if needed
            const evaluationResults: IControlEvaluationResult[] = [...pkgResult, ...deliveryTypeResult, ...otherControlResult];

            //Remove any duplicates
            const combinedEvaluationList: IControlEvaluationResult[] = Array.from(
                new Set(evaluationResults.map(item => JSON.stringify(item)))).map(str => JSON.parse(str));

            setAllEvaluationResults([...combinedEvaluationList]);
            setFailedEvaluationResults(combinedEvaluationList.filter(x => !x.result));

        }
        catch (error) {
            console.error('Error fetching data:', error);
        }
        finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        handleEvaluationResults(initialPackageList, initialDeliveryTypeList, initialOtherTypeList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (failedEvaluationResults.length > 0) {
            setControlEvaluationResults([...failedEvaluationResults]);
        }
    }, [failedEvaluationResults]);

    // Filter evaluation results based on ShowAll button selection
    useEffect(() => {
        if (selected && allEvaluationResults.length > 0)
            setControlEvaluationResults([...allEvaluationResults]);
        else if (!selected && failedEvaluationResults.length > 0)
            setControlEvaluationResults([...failedEvaluationResults]);
    }, [selected, allEvaluationResults, failedEvaluationResults]);


    const evaluateData = async (controlType: string, data: IPackage[] | IDeliveryType[] | IOtherControl[]): Promise<IControlEvaluationResult[]> => {
        const evaluationList: IControlEvaluationResult[] = [];

        const promises = data.map(async (item: IPackage | IDeliveryType | IOtherControl) => {  // Create an array of promises
            const controlOptions = GetControlOptions(item, CONFIGURATION.PACKAGE, currentAppState) //Check for Security access

            evaluationList.push({
                id: item.Id ?? "",
                group: "Security",
                message: "Security Access",
                result: controlOptions.IsEnabled ?? true,
                controlType: controlType,
                originalExpression: null,
                expressionDescription: null
            });
            if (item.Expressions && item.Expressions.length > 0) {
                const validationResults = await validationExpressions(item.Expressions);
                const validResults = validationResults && validationResults.result?.filter(x => !x.error);

                if (validResults && validResults.length > 0) {
                    item.Expressions.filter(ex => ex.ExpressionName && ex.Enabled).forEach((expression: IExpression) => {
                        const evalResult = validResults.find(res => res.expression === addRegexExp(expression.ExpressionString, enhancedFieldIdValuePairs, ""));

                        const controlEvalResult: IControlEvaluationResult = {
                            id: item.Id ?? "",
                            group: "Expression",
                            message: expression.ExpressionName,
                            result: (evalResult && evalResult.evaluationResult && !evalResult.error) ?? false,
                            controlType: controlType,
                            originalExpression: expression.ExpressionString,
                            expressionDescription: expression.ExpressionMessage
                        };
                        evaluationList.push(controlEvalResult);
                    });
                }
                return evaluationList;
            };
        });
        await Promise.all(promises); // Await all promises
        return evaluationList;
    };

    const validationExpressions = async (expressions: IExpression[]): Promise<IExpressionValidationResult> => {
        const results: IExpression[] = await GetExpressionEvaluationResults(expressions, enhancedFieldIdValuePairs, "");
        const expressionArray = results.map((exp: IExpression) => exp.ExpressionString);
        return await expService.validateExpressions(accessToken, expressionArray);
    };

    const getUpdatedPackageId = (pkgList: IPackage[]): IPackage[] => {
        return pkgList.map((pkg: IPackage) => {
            return {
                ...pkg,
                Id: (!pkg.ParentPackageId) ? pkg.Id.trim() :
                    pkg.AlternatePackageId?.trim() + " [" + pkg.Id.trim() + "]"
            }
        })
    }

    const handleViewExpressionEditor = (expressionEval: IControlEvaluationResult) => {
        //filter the matching expressions from packages, deliveries and other controls based on control type, expression Id and expression string as identifiers 
        const matchingPackages = initialPackageList?.filter(x => x.Expressions && x.Expressions.length > 0
            && x.Id === expressionEval?.id && expressionEval.controlType === CONFIGURATION.PACKAGE);
        const matchingDeliveryTypes = initialDeliveryTypeList?.filter(x => x.Expressions && x.Expressions.length > 0
            && x.Id === expressionEval?.id && expressionEval.controlType === CONFIGURATION.DELIVERY_TYPE);
        const matchingOtherControlTypes = initialOtherTypeList?.filter(x => x.Expressions && x.Expressions.length > 0
            && x.Id === expressionEval?.id && expressionEval?.controlType === CONFIGURATION.GENERIC_CONTROL);

        const combinedMatchingResults = [...matchingPackages, ...matchingDeliveryTypes, ...matchingOtherControlTypes];
        if (!combinedMatchingResults || combinedMatchingResults.length === 0) {
            return;
        }
        let foundExpression = null;
        for (const result of combinedMatchingResults) {
            foundExpression = result.Expressions?.filter(exp => exp.ExpressionString).find(exp =>
                exp.ExpressionString.trim() === expressionEval?.originalExpression?.trim() &&
                exp.ExpressionName.trim() === expressionEval?.message?.trim()
            );
            if (foundExpression)
                break;
        };

        if (foundExpression) {
            const updatedExpression = {
                ...foundExpression,
                Enabled: expressionEval.result,
            };
            setSelectedExpression(updatedExpression);
            setOpenViewExpression(true);
        }
    };

    const handleCustomRowClick = (selectedRow: IControlEvaluationResult) => {
        if (footerExpDescription) {
            setFooterExpDescription("");
        }
        setSelectedControlTypeRow(selectedRow);
        handleViewExpression(selectedRow);
    };

    const handleViewExpression = (selectedResult: IControlEvaluationResult) => {
        if (selectedResult.group === "Expression") {
            setViewExpressionEnabled(true);
            if (selectedResult.expressionDescription) {
                setFooterExpDescription(selectedResult.expressionDescription);
            }
        }
        else
            setViewExpressionEnabled(false);
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleToggle = () => {
        setSelected(prevSelected => !prevSelected);
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <DtTabs
                    tabs={[
                        { label: "Evaluations" },
                        { label: "Valid Delivery Methods" }
                    ]}
                    value={tabValue}
                    onChange={handleTabChange}
                    tabPanelSx={{ pt: 0 }}
                >
                    {/* Evaluations Tab */}
                    <Stack direction="column">
                        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
                            <DtLink 
                                onClick={handleToggle}
                                colorVariant="primary"
                                size="medium"
                                disabled={loading}
                            >
                                {selected ? 'Show Failed Only' : 'Show All'}
                            </DtLink>

                            <DtLink 
                                onClick={() => handleViewExpressionEditor(selectedControlTypeRow)}
                                disabled={!viewExpressionEnabled}
                                colorVariant={viewExpressionEnabled ? "primary" : "secondary"}
                                size="medium"
                                sx={{
                                    minWidth: '120px',
                                    textAlign: 'left'
                                }}
                            >
                                View Expression
                            </DtLink>
                        </Stack>
                        <DtDataGrid
                            paged={true}
                            data={controlEvaluationResults}
                            responsivePageConfig={{heightRatio: 0.85}}
                            responsivePageSize={true}
                            columns={evaluationResultsColumns()}
                            dense
                            containerSx={{minHeight: "56vh"}}
                            tableName="Evaluations"
                            onRowSelect={handleCustomRowClick}
                            highlightSelectedRow={true}
                            loading={loading}
                        />
                    </Stack>

                    {/* Valid Delivery Methods Tab */}
                    {tabValue === 1 && (
                        <DtDataGrid
                            paged={true}
                            data={validDeliveryMethods}
                            dense
                            columns={validDeliveryMethodsColumns()}
                            tableName="ValidDeliveryMethods"
                            containerSx={{ minHeight: "53.5vh", maxHeight: "60vh" }}
                            highlightSelectedRow={true}
                            pageSize={10}
                            loading={loading}
                        />
                    )}

                </DtTabs>

                {
                    tabValue === 0 && footerExpDescription &&
                    <DtAlert severity='info' sx={{ position: 'absolute', bottom: '0.7rem', left:'1rem' }}>
                        <Typography>{footerExpDescription}</Typography>
                    </DtAlert>
                }

                <DtDialog
                    open={openViewExpression}
                    onClose={() => setOpenViewExpression(false)}
                    title="Expression Editor"
                    maxWidth="lg"
                    fullWidth
                    
                >
                    <ExpressionEditor
                        expression={selectedExpression}
                        editorMode='view'
                    />
                </DtDialog>
            </Box>
        </>
    );
};

export default EvaluationResults;